import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import type { ProjectFileResponseDto } from '@inference.engine/workspace-api-client/web';
import { Skeleton } from '@monksflow/monks-ui';
import { Check } from 'lucide-react';
import { ROUTES } from '@/shared/config/routes';
import { AssetThumbnail } from '@/entities/project-file';
import { FilterChipPopover } from '@/shared/ui/FilterChipPopover';
import { FILE_STATUS_CONFIG } from '../../../Folders/folders.constants';
import { useTranslation } from 'react-i18next';

const FILMSTRIP_SKELETON_COUNT = 6;

interface Props {
  files: ProjectFileResponseDto[];
  currentFileId: string;
  projectId: string;
  folderId: string;
  isLoading?: boolean;
}

type StatusFilter = 'to_review' | 'approved';

// eslint-disable-next-line i18next/no-literal-string -- URL query parameter name
const STATUS_QUERY_PARAM = 'status';

const FILTER_OPTIONS = [
  { value: 'to_review', label: FILE_STATUS_CONFIG.to_review.label },
  { value: 'approved', label: FILE_STATUS_CONFIG.approved.label },
] as const satisfies ReadonlyArray<{ value: StatusFilter; label: string }>;

function parseStatusFilter(raw: string | null): StatusFilter | null {
  return raw === 'to_review' || raw === 'approved' ? raw : null;
}

export function AssetFilmstrip(props: Props) {
  const { t } = useTranslation('projects');

  const { files, currentFileId, projectId, folderId, isLoading = false } = props;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeRef = useRef<HTMLButtonElement>(null);

  const statusFilter = parseStatusFilter(searchParams.get(STATUS_QUERY_PARAM));

  useEffect(() => {
    activeRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [currentFileId]);

  const goTo = (fileId: string) => {
    // Preserve the active filter (and any other query params) when navigating
    // between siblings — otherwise clicking a thumbnail resets the filter chip.
    const search = searchParams.toString();
    const path = ROUTES.PROJECT_FILE_DETAIL(projectId, folderId, fileId);
    void navigate(search ? `${path}?${search}` : path);
  };

  const handleFilterChange = (next: StatusFilter | null) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next) {
          params.set(STATUS_QUERY_PARAM, next);
        } else {
          params.delete(STATUS_QUERY_PARAM);
        }
        return params;
      },
      { replace: true },
    );
  };

  // Render the strip whenever a filter is active (so the chip remains
  // available to clear it), while loading (so the spinner is visible), or
  // whenever there are siblings to navigate to.
  if (!statusFilter && !isLoading && files.length <= 1) return null;

  return (
    <div className="relative flex w-full flex-col gap-0.5 rounded-bl-lg rounded-br-lg bg-card p-4 pt-1">
      <div className="flex">
        <FilterChipPopover<StatusFilter>
          mode="single"
          label={t('projectDetail.assetFilmstrip.status')}
          variant="ghost"
          triggerLabelStyle="value"
          triggerClassName="p-1"
          popoverHeader={t('projectDetail.assetFilmstrip.filterByStatus')}
          side="top"
          searchable={false}
          options={FILTER_OPTIONS}
          value={statusFilter}
          onChange={handleFilterChange}
        />
      </div>

      <div
        // p-1 is the offset ring's room on every side: overflow-x clips both axes,
        // so without it the selected thumbnail's ring is cut top and bottom, and the
        // first and last items lose theirs to the inline edges too.
        className="-m-1 flex min-w-0 items-center gap-2 overflow-x-auto p-1 scroll-smooth"
        aria-busy={isLoading || undefined}
        aria-live={isLoading ? 'polite' : undefined}
        data-testid={isLoading ? 'asset-filmstrip-skeleton' : undefined}
      >
        {isLoading ? (
          Array.from({ length: FILMSTRIP_SKELETON_COUNT }).map((_, index) => (
            <Skeleton key={index} className="size-18 shrink-0 rounded-lg" />
          ))
        ) : files.length === 0 ? (
          <p className="px-2 py-4 text-sm text-muted-foreground">
            {t('projectDetail.assetFilmstrip.noFilesMatchThisFilter')}
          </p>
        ) : (
          files.map((file) => {
            const isActive = file.id === currentFileId;
            const isApproved = file.status === 'approved';

            return (
              <button
                key={file.id}
                ref={isActive ? activeRef : undefined}
                type="button"
                onClick={() => goTo(file.id)}
                className={`relative shrink-0 overflow-hidden rounded-lg bg-surface ${
                  isActive ? 'ring-2 ring-primary ring-offset-2 ring-offset-card' : 'shadow-lg'
                }`}
              >
                <AssetThumbnail file={file} className="size-18" fit="contain" />
                {isApproved && (
                  <Check
                    aria-label={t('projectDetail.assetFilmstrip.approved')}
                    className="absolute right-1 p-1 top-1 flex size-5 rounded-sm bg-positive text-positive-foreground shadow"
                  />
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
